import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from './supabaseClient';

const serviceSlugs = [
  'groups-fits',
  'religious-cultural',
  'cruises-shore',
  'incentives-corporate',
  'cultural-thematic',
  'sports-tourism',
  'vip-luxury',
];

const Header = () => {
  const { t, i18n } = useTranslation('nav');

  const changeLanguage = (lang) => i18n.changeLanguage(lang);
  const currentLang = i18n.language;

  // SECTIONS computed από translations — ανανεώνεται αυτόματα με κάθε αλλαγή γλώσσας
  const SECTIONS = [
    {
      id: 'services',
      label: t('services'),
      links: serviceSlugs.map(slug => ({
        to: `/services/${slug}`,
        label: t(`serviceLinks.${slug}`),
      })),
    },
    {
      id: 'packages',
      label: t('packages'),
      links: [],
    },
    {
      id: 'tours',
      label: t('tours'),
      links: [{ to: '/tours', label: t('allTours') }],
    },
  ];

  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const [isHidden,      setIsHidden]      = useState(false);
  const [logoVariant,   setLogoVariant]   = useState('light');
  const [panelOpen,     setPanelOpen]     = useState(false);
  const [openSection,   setOpenSection]   = useState('services');
  const [menuOpen,      setMenuOpen]      = useState(false);
  const [packages,      setPackages]      = useState([]);
  const [tours,         setTours]         = useState([]);
  const lastScrollY = useRef(0);
  const panelRef    = useRef(null);
  const navRef      = useRef(null);

  useEffect(() => {
    supabase
      .from('packages')
      .select('slug, title')
      .order('price', { ascending: true })
      .then(({ data }) => { if (data) setPackages(data); });

    supabase
      .from('tours')
      .select('id, title')
      .order('created_at', { ascending: true })
      .then(({ data }) => { if (data) setTours(data); });
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const scrollingUp = y < lastScrollY.current;
      setIsScrollingUp(scrollingUp && y > 50);
      setIsHidden(y > lastScrollY.current && y > 100);
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll('[data-logo]');
    if (!sections.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) setLogoVariant(entry.target.dataset.logo);
      });
    }, { threshold: 0.3, rootMargin: '-10% 0px -60% 0px' });
    sections.forEach(s => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        navRef.current   && !navRef.current.contains(e.target)
      ) {
        setPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  useEffect(() => {
    document.body.style.overflow = panelOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [panelOpen]);

  const handleNavBtn = (id) => {
    if (panelOpen && openSection === id) {
      setPanelOpen(false);
    } else {
      setPanelOpen(true);
      setOpenSection(id);
    }
  };

  const btnBase = {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: '15px',
    fontWeight: '500',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    height: '45px',
    padding: '0 24px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'background-color 0.2s',
  };

  const langs = [
    { code: 'en',  label: 'EN'   },
    { code: 'es',  label: 'ESP'  },
    { code: 'pt',  label: 'PORT' },
    { code: 'gr',  label: 'GR'   },
  ];

  return (
    <>
      {/* ── HEADER ── */}
      <header
        className={`fixed w-full top-0 z-[10000] transition-all duration-300 bg-transparent ${
          isScrollingUp ? 'py-5' : 'py-6'
        }`}
        style={{
          transform: isHidden ? 'translateY(-100%)' : 'translateY(0)',
          transition: 'transform 0.4s ease, background-color 0.3s ease, padding 0.3s ease',
        }}
      >
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between relative">

            {/* LEFT: nav buttons */}
            <div ref={navRef} className="hidden md:flex items-center gap-2">
              {SECTIONS.map(s => (
                <button
                  key={s.id}
                  onClick={() => handleNavBtn(s.id)}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#BE9557'; e.currentTarget.style.color = 'white'; }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = panelOpen && openSection === s.id
                      ? 'rgba(30,41,59,0.7)'
                      : 'rgba(30,41,59,0.4)';
                    e.currentTarget.style.color = 'white';
                  }}
                  style={{
                    ...btnBase,
                    backgroundColor: panelOpen && openSection === s.id
                      ? 'rgba(30,41,59,0.7)'
                      : 'rgba(30,41,59,0.4)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(8px)',
                    opacity: panelOpen ? 0 : 1,
                    transition: 'opacity 0s, background-color 0.2s, color 0.2s',
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* CENTER LOGO (desktop) */}
            <div className="hidden md:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <a href="/" className="block transition-opacity duration-300 hover:opacity-80">
                <img
                  src={logoVariant === 'dark' ? '/BlueGoldLogo.png' : '/Logo_4.png'}
                  alt="Grand Star Hellenic"
                  fetchpriority="high"
                  className="h-20 lg:h-24 w-auto object-contain"
                  style={{ transition: 'opacity 0.5s ease' }}
                />
              </a>
            </div>

            {/* RIGHT: ABOUT + CONTACT + LANGUAGE (desktop) */}
            <nav className="hidden md:flex items-center gap-2">
              {[
                { href: '/about',   label: t('about')   },
                { href: '/contact', label: t('contact') },
              ].map(item => (
                <a key={item.href} href={item.href}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#BE9557'; e.currentTarget.style.color = 'white'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(30,41,59,0.4)'; e.currentTarget.style.color = 'white'; }}
                  style={{
                    ...btnBase,
                    backgroundColor: 'rgba(30,41,59,0.4)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(8px)',
                    textDecoration: 'none',
                    minWidth: '120px',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s, color 0.2s',
                  }}>
                  {item.label}
                </a>
              ))}

              {/* Language switcher desktop */}
              <div style={{
                display: 'flex', alignItems: 'center', height: '45px', padding: '0 16px',
                backgroundColor: 'rgba(30,41,59,0.4)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.05)',
              }}>
                {langs.map((lang, i) => (
                  <React.Fragment key={lang.code}>
                    <button onClick={() => changeLanguage(lang.code)} style={{
                      fontFamily: 'Montserrat, sans-serif', fontSize: '11px', fontWeight: '500',
                      letterSpacing: '0.12em', padding: '0 6px',
                      opacity: currentLang === lang.code ? 1 : 0.4,
                      color: 'white',
                      background: 'none', border: 'none', cursor: 'pointer', textTransform: 'uppercase',
                    }}>
                      {lang.label}
                    </button>
                    {i < langs.length - 1 && <span style={{ opacity: 0.3, color: 'white', fontSize: '10px' }}>/</span>}
                  </React.Fragment>
                ))}
              </div>
            </nav>

            {/* MOBILE: Menu | Logo | Language */}
            <div className="md:hidden flex items-center w-full" style={{
              border: '0.5px solid rgba(255,255,255,0.2)',
              backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
              backgroundColor: 'rgba(0,0,0,0.1)',
              height: '48px',
            }}>
              <button onClick={() => setMenuOpen(true)} style={{
                fontFamily: 'Montserrat, sans-serif', fontSize: '11px', fontWeight: '500',
                letterSpacing: '0.08em', textTransform: 'uppercase',
                color: 'white',
                background: 'none', border: 'none', cursor: 'pointer',
                width: '5rem', height: '100%', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>{t('menu')}</button>

              <a href="/" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
                className="hover:opacity-80 transition-opacity duration-300">
                <img src="/BlueGoldLogo.png" alt="Grand Star Hellenic" fetchpriority="high"
                  style={{ height: '34px', width: 'auto', objectFit: 'contain', display: 'block' }} />
              </a>

              {/* Mobile language cycle */}
              <button
                onClick={() => {
                  const codes = langs.map(l => l.code);
                  changeLanguage(codes[(codes.indexOf(currentLang) + 1) % codes.length]);
                }}
                style={{
                  fontFamily: 'Montserrat, sans-serif', fontSize: '11px', fontWeight: '500',
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  color: 'white',
                  background: 'none', border: 'none', cursor: 'pointer',
                  width: '5rem', height: '100%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                {langs.find(l => l.code === currentLang)?.label || (currentLang || 'en').toUpperCase()}
              </button>
            </div>

          </div>
        </div>
      </header>

      {/* ── DIM OVERLAY ── */}
      <div
        style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(7,29,61,0.35)',
          backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
          opacity: panelOpen ? 1 : 0,
          pointerEvents: panelOpen ? 'auto' : 'none',
          transition: 'opacity 0.35s ease',
          zIndex: 10002,
        }}
      />

      {/* ── DESKTOP PANEL ── */}
      <div
        ref={panelRef}
        style={{
          position: 'fixed',
          left: '8px', top: '8px', bottom: '8px',
          width: '680px',
          backgroundColor: 'rgb(249,245,233)',
          borderRadius: '12px',
          boxShadow: '8px 0 48px rgba(0,0,0,0.12)',
          overflow: 'hidden',
          pointerEvents: panelOpen ? 'auto' : 'none',
          clipPath: panelOpen
            ? 'inset(0% 0% 0% 0% round 12px)'
            : 'inset(0% 100% 0% 0% round 12px)',
          transition: 'clip-path 0.4s cubic-bezier(0.5,1,0.89,1)',
          zIndex: 10003,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Nav buttons row inside panel */}
        <div style={{
          display: 'flex', gap: '8px',
          padding: '22px 0 0 0', marginLeft: '32px', flexShrink: 0,
        }}>
          {SECTIONS.map(s => (
            <button
              key={s.id}
              onClick={() => handleNavBtn(s.id)}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#BE9557'; e.currentTarget.style.color = 'white'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = openSection === s.id ? 'rgba(7,29,61,0.18)' : 'rgba(7,29,61,0.08)'; e.currentTarget.style.color = '#071d3d'; }}
              style={{
                ...btnBase,
                backgroundColor: openSection === s.id ? 'rgba(7,29,61,0.18)' : 'rgba(7,29,61,0.08)',
                color: '#071d3d',
                border: 'none',
                transition: 'background-color 0.2s, color 0.2s',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Accordion content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0 24px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {SECTIONS.map((section, si) => (
            <div key={section.id}>
              {si > 0 && (section.id !== 'tours' || openSection === 'packages') && (
                <div style={{ height: '1px', margin: '0.75em 1.5em', backgroundColor: 'rgba(7,29,61,0.12)' }} />
              )}

              <button
                onClick={() => setOpenSection(openSection === section.id ? null : section.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '2em 1.5em',
                  fontFamily: '"Cormorant Garamond", var(--font-display), serif',
                  fontSize: '19px', fontStyle: 'italic', fontWeight: '400', lineHeight: '1.2',
                  color: openSection === section.id ? 'rgba(7,29,61,0.9)' : 'rgba(7,29,61,0.5)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  transition: 'color 0.3s cubic-bezier(0.5,1,0.89,1)',
                }}
              >
                {section.label}
              </button>

              <div style={{
                maxHeight: openSection === section.id ? '600px' : '0px',
                overflow: 'hidden',
                transition: 'max-height 0.35s cubic-bezier(0.5,1,0.89,1)',
              }}>
                <div style={{ padding: '4px 32px 12px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {(section.id === 'packages'
                    ? [{ to: '/packages', label: t('allPackages') }, ...packages.map(p => ({ to: `/packages/${p.slug}`, label: p.title }))]
                    : section.id === 'tours'
                    ? [{ to: '/tours', label: t('allTours'), key: 'all-tours' }, ...tours.map(tour => ({ to: '/tours', label: tour.title, key: String(tour.id) }))]
                    : section.links
                  ).map(link => (
                    <Link
                      key={link.key || link.to}
                      to={link.to}
                      onClick={() => setPanelOpen(false)}
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: '23px', fontWeight: '500',
                        color: '#071d3d', lineHeight: '1.35',
                        display: 'block', textDecoration: 'none',
                        transition: 'color 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = '#BE9557'}
                      onMouseLeave={e => e.currentTarget.style.color = '#071d3d'}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 32px', flexShrink: 0,
          fontFamily: 'Montserrat, sans-serif', fontSize: '10px',
          color: 'rgba(7,29,61,0.65)', letterSpacing: '0.1em', textAlign: 'center',
        }}>
          We Shape Expectations. We Deliver Memories.
        </div>
      </div>

      {/* ── MOBILE FULLSCREEN MENU ── */}
      <div
        className="md:hidden fixed inset-0 z-[99999] flex flex-col"
        style={{
          backgroundColor: 'rgb(249,245,233)',
          opacity: menuOpen ? 1 : 0,
          pointerEvents: menuOpen ? 'all' : 'none',
          transition: 'opacity 0.3s ease',
        }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'rgba(7,29,61,0.08)' }}>
          <a href="/" onClick={() => setMenuOpen(false)}>
            <img src="/BlueGoldLogo.png" alt="Grand Star Hellenic"
              style={{ height: '32px', width: 'auto', objectFit: 'contain' }} />
          </a>
          <button onClick={() => setMenuOpen(false)} style={{
            fontFamily: 'Montserrat, sans-serif', fontSize: '11px', fontWeight: '500',
            letterSpacing: '0.08em', textTransform: 'uppercase', color: '#071d3d',
            background: 'rgba(7,29,61,0.06)', border: 'none', cursor: 'pointer', padding: '8px 20px',
          }}>{t('close')}</button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          <div>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontStyle: 'italic', color: 'rgba(7,29,61,0.45)', marginBottom: '12px' }}>{t('services')}</p>
            {serviceSlugs.map(slug => (
              <Link key={slug} to={`/services/${slug}`} onClick={() => setMenuOpen(false)}
                style={{ display: 'block', fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: '300', color: '#071d3d', lineHeight: '1.4', paddingBottom: '2px' }}>
                {t(`serviceLinks.${slug}`)}
              </Link>
            ))}
          </div>
          <div style={{ borderTop: '1px dashed rgba(7,29,61,0.15)' }} />
          <div>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontStyle: 'italic', color: 'rgba(7,29,61,0.45)', marginBottom: '12px' }}>Explore</p>
            <Link to="/packages" onClick={() => setMenuOpen(false)}
              style={{ display: 'block', fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: '300', color: '#071d3d', lineHeight: '1.4' }}>{t('packages')}</Link>
            <Link to="/tours" onClick={() => setMenuOpen(false)}
              style={{ display: 'block', fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: '300', color: '#071d3d', lineHeight: '1.4' }}>{t('tours')}</Link>
          </div>
          <div style={{ borderTop: '1px dashed rgba(7,29,61,0.15)' }} />
          <div>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontStyle: 'italic', color: 'rgba(7,29,61,0.45)', marginBottom: '12px' }}>Company</p>
            <Link to="/about" onClick={() => setMenuOpen(false)}
              style={{ display: 'block', fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: '300', color: '#071d3d', lineHeight: '1.4' }}>{t('about')}</Link>
            <Link to="/contact" onClick={() => setMenuOpen(false)}
              style={{ display: 'block', fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: '300', color: '#071d3d', lineHeight: '1.4' }}>{t('contact')}</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;